import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tabs: {
      insertTabs: () => ReturnType;
      insertPeriods: () => ReturnType;
    };
  }
}

export interface TabsOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

export const Tabs = Node.create<TabsOptions>({
  name: 'tabs',
  group: 'block',
  content: 'tab+',
  defining: true,
  isolating: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      // `periods` turns the block into a recurring-event archive: each tab
      // is one run of the event (a month of the gift calendar) with its own
      // start/end, and the public site shows the current run with
      // ‹ prev / next › arrows to the past ones instead of a tab strip.
      // Same node rather than a new one so every consumer that already
      // understands tabs (server schema, markdown, search) keeps working.
      variant: {
        default: 'tabs',
        parseHTML: (el) =>
          el.getAttribute('data-variant') === 'periods' ? 'periods' : 'tabs',
        renderHTML: (attrs) =>
          attrs.variant === 'periods' ? { 'data-variant': 'periods' } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="tabs"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addCommands() {
    return {
      insertTabs:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            content: [
              {
                type: 'tab',
                attrs: { label: 'Tab 1' },
                content: [{ type: 'paragraph' }],
              },
              {
                type: 'tab',
                attrs: { label: 'Tab 2' },
                content: [{ type: 'paragraph' }],
              },
            ],
          });
        },
      insertPeriods:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { variant: 'periods' },
            content: [
              {
                type: 'tab',
                attrs: { label: 'Period 1', start: null, end: null },
                content: [{ type: 'paragraph' }],
              },
            ],
          });
        },
    };
  },

  addNodeView() {
    if (!this.options.view) return null;
    this.editor.isInitialized = true;
    return ReactNodeViewRenderer(this.options.view);
  },
});
